// The purchase order document (P3.4; 08-DOCUMENTS, mockup 13, D55). One renderer for the in-app
// print view and the HTML the broker converts to PDF: bilingual (English left, Arabic right), A4,
// entity stripe, logo, every field of today's PO, totals in words, payment schedule, terms page,
// approval block and a QR code to the record. Values are escaped; markup uses classes only (the
// app's CSP has no inline styles), and the CSS ships as a string for both uses.

import qrcode from 'qrcode-generator';
import tokens from '../../design-system/tokens.json';
import { unitText } from '../rfq/rfq';
import type { Unit } from '../pr/form';
import { amountInWords } from '../rules/words';
import { entityLogo } from './logo';
import type { Milestone, PaymentTerm, PoLineType } from './po';

export interface PoDocData {
  poNo: string;
  /** YYYY-MM-DD */
  poDate: string;
  prNo: string;
  agreementNo: string;
  projectCode: string;
  quoteRef: string;
  entity: {
    code: string;
    nameEN: string;
    nameAR: string;
    cr: string;
    vat: string;
    address: string;
  };
  supplier: { name: string; nameAR: string; contact: string; mobile: string; email: string };
  delivery: {
    site: string;
    address: string;
    receiver: string;
    phone: string;
    email: string;
    neededBy: string;
  };
  currency: string;
  lines: {
    description: string;
    qty: number;
    unit: string;
    type: PoLineType;
    months: number;
    unitPrice: number;
    total: number;
  }[];
  totals: { monthly: number; subtotal: number; vatRate: number; vat: number; total: number };
  paymentTerm: PaymentTerm;
  schedule: { milestone: Milestone; label: string; pct: number; amount: number }[];
  /** Standard terms (Settings po.terms.en / .ar); draft placeholder text until Finance supplies them. */
  terms: { en: string; ar: string; draft: boolean };
  extraTerms: string;
  preparedBy: string;
  /** Name and job title shown on the document (D104); the address stays in preparedBy. */
  preparedByName?: string;
  preparedByTitle?: string;
  preparedAt: string;
  approval: {
    state: 'approved' | 'pending';
    by: string;
    at: string;
    name?: string;
    title?: string;
  };
  /** The vertical PO approver's approval after the CFO (D114), when there was one. */
  verticalApproval?: { vertical: string; by: string; at: string; name?: string; title?: string };
  verifyUrl: string;
}

/** Shown until Finance supplies the PO terms (open item 5); marked DRAFT on the document. */
export const DRAFT_TERMS = {
  en: [
    'General provisions: this order is governed by the laws of the issuing entity’s country.',
    'Payments: as per the payment schedule, against a valid tax invoice quoting this PO number.',
    'Taxes and Zakat: the seller bears its own taxes and Zakat.',
    'VAT: shown separately at the applicable rate; the seller’s VAT number must be on the invoice.',
    'Delivery: a fee of 5% may be charged for unjustified delay.',
    'Validity: this PO must be accepted within 7 working days of its date.',
  ].join('\n'),
  ar: [
    'أحكام عامة: يخضع هذا الأمر لأنظمة بلد الجهة المُصدِرة.',
    'الدفع: حسب جدول الدفعات، مقابل فاتورة ضريبية صحيحة تذكر رقم أمر الشراء.',
    'الضرائب والزكاة: يتحمل البائع ضرائبه وزكاته.',
    'ضريبة القيمة المضافة: تظهر منفصلة بالنسبة المطبقة، ويجب أن يظهر الرقم الضريبي للبائع على الفاتورة.',
    'التسليم: قد تُفرض غرامة 5% على التأخير غير المبرر.',
    'الصلاحية: يجب قبول أمر الشراء خلال 7 أيام عمل من تاريخه.',
  ].join('\n'),
};

const TERM_TEXT: Record<PaymentTerm, [string, string]> = {
  advance100: ['100% advance', '100% دفعة مقدمة'],
  split50: ['50% advance, 50% on delivery', '50% مقدماً و50% عند التسليم'],
  split25: ['25% advance, 75% on delivery', '25% مقدماً و75% عند التسليم'],
  delivery100: ['100% on delivery', '100% عند التسليم'],
  net30: ['Net 30 days after invoice', 'خلال 30 يوماً من الفاتورة'],
  custom: ['As per the schedule', 'حسب جدول الدفعات'],
};
const MILESTONE_TEXT: Record<Milestone, [string, string]> = {
  advance: ['Advance', 'دفعة مقدمة'],
  delivery: ['On delivery', 'عند التسليم'],
  net30: ['30 days after invoice', 'بعد 30 يوماً من الفاتورة'],
  milestone: ['Milestone', 'مرحلة'],
};

export function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

export const or = (v: string, placeholder: string) => esc(v.trim() || placeholder);

/** The QR code as an SVG path (classes only, no inline style). */
export function qrSvg(text: string): string {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  let d = '';
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c + 2} ${r + 2}h1v1h-1z`;
  }
  return `<svg class="pod-qr" viewBox="0 0 ${n + 4} ${n + 4}" role="img" aria-label="QR code" xmlns="http://www.w3.org/2000/svg"><rect width="${n + 4}" height="${n + 4}" fill="#fff"/><path d="${d}" fill="#000"/></svg>`;
}

export const both = (en: string, ar: string) =>
  `<span class="pod-en">${en}</span><span class="pod-ar" lang="ar" dir="rtl">${ar}</span>`;

export const field = (en: string, ar: string, value: string) =>
  `<div class="pod-field"><span class="pod-label">${en} · <span lang="ar">${ar}</span></span><span class="pod-value">${value}</span></div>`;

const paras = (text: string) =>
  text
    .split(/\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<li>${esc(p)}</li>`)
    .join('');

// Converters (Graph's HTML→PDF) may drop CSS backgrounds; HTML colour attributes survive.
export function stripeHtml(): string {
  return `<table class="pod-stripe" role="presentation"><tr><td bgcolor="${T('entity-smc')}"></td><td bgcolor="${T('entity-vibes')}"></td><td bgcolor="${T('entity-media')}"></td></tr></table>`;
}

/** The document's two pages (body only). */
export function renderPoDocument(d: PoDocData): string {
  const words = {
    subtotal: amountInWords(d.totals.subtotal, d.currency),
    vat: amountInWords(d.totals.vat, d.currency),
    total: amountInWords(d.totals.total, d.currency),
  };
  const cur = esc(d.currency);
  const e = d.entity;
  const pending = d.approval.state === 'pending';
  const STRIPE_HTML = stripeHtml();
  const HEAD_BG = `bgcolor="${T('accent')}"`;
  const lines = d.lines
    .map(
      (l, i) => `<tr>
<td>${i + 1}</td>
<td dir="auto">${esc(l.description)}${l.type === 'monthly' ? `<span class="pod-sub">${esc(`Monthly × ${l.months} months`)} · <span lang="ar">شهري × ${l.months} أشهر</span></span>` : l.type === 'milestone' ? `<span class="pod-sub">Milestone · <span lang="ar">مرحلة</span></span>` : ''}</td>
<td class="pod-num">${esc(l.qty)} <span class="pod-sub-inline">${esc(unitText(l.unit as Unit, 'en'))}${l.type === 'monthly' ? ' / month' : ''}</span></td>
<td class="pod-num">${money(l.unitPrice)}</td>
<td class="pod-num">${money(l.total)}</td>
</tr>`,
    )
    .join('');
  const schedule = d.schedule
    .map((s) => {
      const [en, ar] = MILESTONE_TEXT[s.milestone];
      const label = s.milestone === 'milestone' && s.label ? esc(s.label) : both(esc(en), esc(ar));
      return `<tr><td>${label}</td><td class="pod-num">${s.pct}%</td><td class="pod-num">${money(s.amount)}</td></tr>`;
    })
    .join('');
  const [termEn, termAr] = TERM_TEXT[d.paymentTerm];

  return `<article class="pod" lang="en">
<section class="pod-page">
${STRIPE_HTML}
<header class="pod-head">
<div class="pod-brand">${entityLogo(d.entity.code)}</div>
<div class="pod-title"><h1>PURCHASE ORDER</h1><p lang="ar" dir="rtl">أمر شراء</p></div>
</header>
<div class="pod-entity">
<p>${or(e.nameEN, `[${e.code} legal name]`)} · CR ${or(e.cr, '[CR no.]')} · VAT ${or(e.vat, '[VAT no.]')}<br>${or(e.address, '[Registered address]')}</p>
<p lang="ar" dir="rtl">${or(e.nameAR, '[الاسم القانوني]')} · سجل تجاري ${or(e.cr, '[رقم السجل]')} · الرقم الضريبي ${or(e.vat, '[الرقم الضريبي]')}</p>
</div>
<div class="pod-refs">
${field('PO no.', 'رقم أمر الشراء', `<strong>${esc(d.poNo)}</strong>`)}
${field('PO date', 'التاريخ', esc(d.poDate))}
${field('PR no.', 'رقم طلب الشراء', esc(d.prNo))}
${field('Agreement / project', 'الاتفاقية / المشروع', `${or(d.agreementNo, '—')} / ${or(d.projectCode, '—')}`)}
</div>
<p class="pod-note">${both('Quote this PO number on all correspondence, delivery documents and invoices.', 'يرجى ذكر رقم أمر الشراء في جميع المراسلات ومستندات التسليم والفواتير.')}</p>
<div class="pod-parties">
<div class="pod-box"><h2>${both('SELLER', 'البائع')}</h2>
${field('Name', 'الاسم', esc(d.supplier.name))}
${d.supplier.nameAR ? field('Arabic name', 'الاسم بالعربية', esc(d.supplier.nameAR)) : ''}
${field('Contact', 'المسؤول', or(d.supplier.contact, '—'))}
${field('Mobile', 'الجوال', `<span dir="ltr">${or(d.supplier.mobile, '—')}</span>`)}
${field('Email', 'البريد', `<span dir="ltr">${or(d.supplier.email, '—')}</span>`)}
${field('Quote ref.', 'مرجع العرض', `<span dir="ltr">${or(d.quoteRef, '—')}</span>`)}
</div>
<div class="pod-box"><h2>${both('DELIVERY POINT', 'نقطة التسليم')}</h2>
${field('Site', 'الموقع', `${esc(d.delivery.site)}${d.delivery.address ? ` · ${esc(d.delivery.address)}` : ''}`)}
${field('Receiver', 'المستلم', or(d.delivery.receiver, '—'))}
${field('Phone', 'الهاتف', `<span dir="ltr">${or(d.delivery.phone, '—')}</span>`)}
${field('Email', 'البريد', `<span dir="ltr">${or(d.delivery.email, '—')}</span>`)}
${field('Needed by', 'مطلوب قبل', or(d.delivery.neededBy, '—'))}
</div>
</div>
<p class="pod-statement">${both('This order is subject to the terms and conditions of the issuing entity, the information agreed in advance, and any special terms attached.', 'يخضع هذا الأمر لشروط وأحكام الجهة المُصدِرة والمعلومات المتفق عليها مسبقاً، وأي شروط خاصة مرفقة.')}</p>
<table class="pod-lines">
<thead><tr><th ${HEAD_BG}>#</th><th ${HEAD_BG}>${both('Description', 'الوصف')}</th><th ${HEAD_BG} class="pod-num">${both('Qty', 'الكمية')}</th><th ${HEAD_BG} class="pod-num">${both(`Unit (${cur})`, 'السعر')}</th><th ${HEAD_BG} class="pod-num">${both(`Total (${cur})`, 'الإجمالي')}</th></tr></thead>
<tbody>${lines}</tbody>
</table>
<div class="pod-totals">
${
  d.totals.monthly > 0
    ? `<div class="pod-total"><div>${both(`Monthly total (${cur})`, 'الإجمالي الشهري')}</div><div class="pod-num">${money(d.totals.monthly)}</div></div>`
    : ''
}
<div class="pod-total"><div>${both(`Subtotal (${cur})`, 'الإجمالي قبل الضريبة')}<span class="pod-words">${esc(words.subtotal.en)}</span><span class="pod-words" lang="ar" dir="rtl">${esc(words.subtotal.ar)}</span></div><div class="pod-num">${money(d.totals.subtotal)}</div></div>
<div class="pod-total"><div>${both(`VAT ${esc(d.totals.vatRate)}%`, `ضريبة القيمة المضافة ${esc(d.totals.vatRate)}%`)}<span class="pod-words">${esc(words.vat.en)}</span><span class="pod-words" lang="ar" dir="rtl">${esc(words.vat.ar)}</span></div><div class="pod-num">${money(d.totals.vat)}</div></div>
<div class="pod-total pod-total--grand"><div>${both(`Total incl. VAT (${cur})`, 'الإجمالي شامل الضريبة')}<span class="pod-words">${esc(words.total.en)}</span><span class="pod-words" lang="ar" dir="rtl">${esc(words.total.ar)}</span></div><div class="pod-num">${money(d.totals.total)}</div></div>
</div>
<div class="pod-payment">
<p>${both(`<strong>Payment:</strong> ${esc(termEn)}`, `<strong>الدفع:</strong> ${esc(termAr)}`)}</p>
<table class="pod-schedule"><thead><tr><th>${both('Payment', 'الدفعة')}</th><th class="pod-num">%</th><th class="pod-num">${both(`Amount (${cur})`, 'المبلغ')}</th></tr></thead><tbody>${schedule}</tbody></table>
</div>
<footer class="pod-sign${d.verticalApproval ? ' pod-sign--three' : ''}">
<div class="pod-sign__box"><span class="pod-label">PREPARED BY · PROCUREMENT · <span lang="ar">إعداد · المشتريات</span></span><strong>${esc(d.preparedByName || d.preparedBy)}</strong>${d.preparedByTitle ? `<span>${esc(d.preparedByTitle)}</span>` : ''}<span dir="ltr">${esc(d.preparedAt)}</span></div>
<div class="pod-sign__box ${pending ? 'pod-sign__box--pending' : 'pod-sign__box--ok'}"><span class="pod-label">APPROVED BY · CFO · <span lang="ar">اعتماد · المدير المالي</span></span>${
    pending
      ? `<strong>Issued before CFO signature, pending</strong><span lang="ar" dir="rtl">صدر قبل توقيع المدير المالي، بانتظار التوقيع</span>`
      : `<strong>${esc(d.approval.name || d.approval.by)}</strong>${d.approval.title ? `<span>${esc(d.approval.title)}</span>` : ''}<span>Digitally approved · <span lang="ar" dir="rtl">معتمد إلكترونياً</span></span><span dir="ltr">${esc(d.approval.at)}</span>`
  }</div>${
    d.verticalApproval
      ? `\n<div class="pod-sign__box pod-sign__box--ok"><span class="pod-label">APPROVED BY · ${esc(d.verticalApproval.vertical.toUpperCase())} · <span lang="ar">اعتماد القطاع</span></span><strong>${esc(d.verticalApproval.name || d.verticalApproval.by)}</strong>${d.verticalApproval.title ? `<span>${esc(d.verticalApproval.title)}</span>` : ''}<span>Digitally approved · <span lang="ar" dir="rtl">معتمد إلكترونياً</span></span><span dir="ltr">${esc(d.verticalApproval.at)}</span></div>`
      : ''
  }
<div class="pod-qr-box">${qrSvg(d.verifyUrl)}<span dir="ltr">${esc(d.poNo)}</span></div>
</footer>
<p class="pod-foot"><span>Page 1 of 2 · Terms and conditions overleaf · <span lang="ar">الشروط والأحكام في الصفحة التالية</span></span><span>Generated by Procure</span></p>
</section>
<section class="pod-page pod-page--terms">
${STRIPE_HTML}
<h2 class="pod-terms-title">${both('Terms and conditions', 'الشروط والأحكام')}${d.terms.draft ? ' <span class="pod-draft">DRAFT · مسودة</span>' : ''}</h2>
<div class="pod-terms">
<ol>${paras(d.terms.en)}</ol>
<ol lang="ar" dir="rtl">${paras(d.terms.ar)}</ol>
</div>
${d.extraTerms ? `<h3>${both('Special conditions', 'شروط خاصة')}</h3><p class="pod-extra" dir="auto">${esc(d.extraTerms)}</p>` : ''}
<p class="pod-foot"><span>Page 2 of 2 · <span dir="ltr">${esc(d.poNo)}</span></span><span>Generated by Procure</span></p>
</section>
</article>`;
}

/** A design token's light value (design-system/tokens.json): documents are printed on white. */
export function T(name: string): string {
  const token = (tokens.color.tokens as { name: string; value: string | { light: string } }[]).find(
    (t) => t.name === name,
  );
  if (!token) throw new Error(`Unknown token ${name}`);
  return typeof token.value === 'string' ? token.value : token.value.light;
}

/** Document styles, shared by the print view (adopted stylesheet) and the PDF HTML. */
export const PO_CSS = `
.pod{-webkit-print-color-adjust:exact;print-color-adjust:exact;font-family:"IBM Plex Sans Arabic","Segoe UI",Arial,sans-serif;color:${T('logo-ink')};font-size:10.5px;line-height:1.45}
.pod-page{box-sizing:border-box;width:210mm;min-height:297mm;margin:0 auto 16px;padding:12mm 12mm 10mm;background:${T('surface-card')};position:relative;display:flex;flex-direction:column;gap:8px}
.pod-page--terms{page-break-before:always;break-before:page}
.pod-stripe{width:calc(100% + 24mm);height:4px;margin:-12mm -12mm 6mm;border-collapse:collapse;table-layout:fixed}
.pod-stripe td{height:4px;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.pod-stripe td:nth-child(1){background:${T('entity-smc')}}
.pod-stripe td:nth-child(2){background:${T('entity-vibes')}}
.pod-stripe td:nth-child(3){background:${T('entity-media')}}
.pod-head{display:flex;justify-content:space-between;align-items:flex-start}
.pod-logo{width:120px;height:auto}
.pod-title{text-align:right}
.pod-title h1{margin:0;font-size:20px;letter-spacing:.04em}
.pod-title p{margin:0;font-size:18px;font-weight:700}
.pod-entity{display:flex;justify-content:space-between;gap:12px;border-bottom:1px solid ${T('border-strong')};padding-bottom:6px;color:${T('ink-2')}}
.pod-entity p{margin:0}
.pod-refs{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;border:1px solid ${T('border-strong')};border-radius:6px;padding:6px 8px}
.pod-field{display:flex;flex-direction:column;gap:1px;padding:2px 0}
.pod-box .pod-field{flex-direction:row;gap:8px}
.pod-box .pod-label{min-width:90px}
.pod-label{font-size:8.5px;text-transform:uppercase;letter-spacing:.03em;color:${T('ink-muted')}}
.pod-value{font-family:"IBM Plex Mono",Consolas,monospace}
.pod-box .pod-value{font-family:inherit}
.pod-note,.pod-statement{display:flex;justify-content:space-between;gap:12px;margin:0;color:${T('ink-2')}}
.pod-statement{background:${T('surface-page')};padding:6px 8px;border-radius:6px}
.pod-en{flex:1}
.pod-ar{flex:1;text-align:right}
.pod-parties{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.pod-box{border:1px solid ${T('border-strong')};border-radius:6px;padding:6px 8px}
.pod-box h2{display:flex;justify-content:space-between;margin:0 0 4px;font-size:10px;color:${T('accent')}}
.pod-lines,.pod-schedule{width:100%;border-collapse:collapse}
.pod-lines th{-webkit-print-color-adjust:exact;print-color-adjust:exact;background:${T('accent')};color:${T('on-accent')};text-align:left;padding:5px 6px;font-size:9px}
.pod-lines th .pod-en,.pod-lines th .pod-ar,.pod-schedule th .pod-en,.pod-schedule th .pod-ar{display:block;flex:none;text-align:inherit}
.pod-lines td{border-bottom:1px solid ${T('border')};padding:5px 6px;vertical-align:top}
.pod-schedule th{text-align:left;border-bottom:1px solid ${T('border-strong')};padding:3px 6px;font-size:9px}
.pod-schedule td{padding:3px 6px;border-bottom:1px solid ${T('border-subtle')}}
.pod-schedule td .pod-en,.pod-schedule td .pod-ar{display:inline}
.pod-schedule td .pod-ar{margin-left:8px}
.pod-num{text-align:right!important;font-family:"IBM Plex Mono",Consolas,monospace;white-space:nowrap}
.pod-sub{display:block;color:${T('ink-muted')};font-size:9px}
.pod-sub-inline{color:${T('ink-muted')};font-size:9px;font-family:inherit}
.pod-totals{display:grid;gap:0;border:1px solid ${T('border-strong')};border-radius:6px;overflow:hidden}
.pod-total{display:flex;justify-content:space-between;gap:12px;padding:6px 8px;border-bottom:1px solid ${T('border')}}
.pod-total>div:first-child{display:flex;flex-wrap:wrap;flex:1;gap:0 12px}
.pod-total .pod-en,.pod-total .pod-ar{font-weight:700;flex:1 1 40%}
.pod-words{flex:1 1 40%;color:${T('ink-muted')};font-size:9px}
.pod-words[lang=ar]{text-align:right}
.pod-total--grand{background:${T('accent-soft')};border-bottom:0}
.pod-total--grand .pod-num{font-size:13px;font-weight:700}
.pod-payment p{display:flex;justify-content:space-between;margin:0 0 4px}
.pod-sign{display:grid;grid-template-columns:1fr 1fr auto;gap:8px;margin-top:auto;align-items:stretch}
.pod-sign--three{grid-template-columns:1fr 1fr 1fr auto}
.pod-sign__box{display:flex;flex-direction:column;gap:2px;border:1px solid ${T('border-strong')};border-radius:6px;padding:6px 8px}
.pod-sign__box--ok{border-color:${T('accent')};background:${T('accent-soft')}}
.pod-sign__box--pending{border-color:${T('warning-panel-border')};background:${T('warning-panel')}}
.pod-qr-box{display:flex;flex-direction:column;align-items:center;font-size:8px;font-family:"IBM Plex Mono",Consolas,monospace}
.pod-qr{width:72px;height:72px}
.pod-foot{display:flex;justify-content:space-between;margin:0;color:${T('ink-muted')};font-size:8.5px;border-top:1px solid ${T('border')};padding-top:4px}
.pod-page--terms .pod-foot{margin-top:auto}
.pod-terms-title{display:flex;justify-content:space-between;align-items:center;font-size:12px;margin:0}
.pod-terms{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.pod-terms ol{margin:0;padding-inline-start:16px}
.pod-terms li{margin-bottom:4px}
.pod-draft{display:inline-block;margin-inline-start:8px;padding:1px 6px;border:1px solid ${T('danger-ink')};color:${T('danger-ink')};font-size:9px;letter-spacing:.06em}
.pod-extra{white-space:pre-wrap;margin:0}
.pod-page h3{display:flex;justify-content:space-between;font-size:11px;margin:8px 0 0}
.pod [lang=ar]{font-family:"IBM Plex Sans Arabic","Segoe UI",Tahoma,sans-serif}
@page{size:A4;margin:0}
@media print{.pod-page{margin:0;box-shadow:none}}
`;

/** A complete HTML file for the PDF converter: the document plus its styles. */
export function poHtmlFile(d: PoDocData): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(d.poNo)}</title><style>${PO_CSS}</style></head><body>${renderPoDocument(d)}</body></html>`;
}
